import { useEffect, useMemo, useState } from 'react';

import { LayerContext } from 'state/LayerContext';
import { useGetTypicalMonthsQuery } from 'state/apiSlice';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import {
  selectMaximize,
  selectProject,
  selectSegmentId,
  selectTypicalMonth,
  selectVizMode,
  setTypicalMonth,
} from 'state/workflowSlice';

import { VIZ_MODE_CHART } from 'appConstants';
import { PlanningChartVolume } from 'features/chart/PlanningChartVolume';
import ScreenshotChart from 'features/chart/ScreenshotChart';
import { Map } from 'features/map/Map';
import { withSegmentTooltip } from 'features/map/hooks/withSegmentTooltip';
import { PlanningTableVolume } from 'features/table/PlanningTableVolume';
import { BinSizePicker } from 'features/task_bar/BinSizePicker';
import { PlanningMonthPicker } from 'features/task_bar/PlanningMonthPicker';
import { TaskBar } from 'features/task_bar/TaskBar';
import { CapacityThresholdEditor } from 'features/workflow_planning/CapacityThresholdEditor';
import { PlanningChartHeader } from 'features/workflow_planning/PlanningChartHeader';
import { workflowStyles } from 'features/workspace/workspaceUtils';

import { MapContextProvider } from '../map/MapContext';
import { ReliesOn } from '../workflow_common/NoDataLoadedWarning';
import {
  WorkflowInfo,
  maybeCarryOverSelection,
} from '../workflow_common/workflowLookup';
import { NoSegmentSelectedPanel } from '../workspace/NoSegmentSelectedPanel';

import styles from '../workspace/Workspace.module.css';

export const workflowPlanning: WorkflowInfo = {
  dataAvailableReliesOn: [ReliesOn.planningMonth],
  switchToState: (fromWorkflow, state) => {
    maybeCarryOverSelection(state, fromWorkflow);
  },
};

function VisualisationRegion({ overrideVizMode }: { overrideVizMode?: any }) {
  const maximize = useAppSelector(selectMaximize);
  const vizMode = overrideVizMode ?? useAppSelector(selectVizMode);
  const segmentId = useAppSelector(selectSegmentId);

  const { data_viz_style, header_style } = workflowStyles(maximize);

  if (segmentId === undefined) {
    return <NoSegmentSelectedPanel />;
  }

  return (
    <div className={data_viz_style}>
      <div className={header_style}>
        <PlanningChartHeader />
      </div>
      <div className={styles.chart_container_inner}>
        {vizMode === VIZ_MODE_CHART ? (
          <PlanningChartVolume />
        ) : (
          <PlanningTableVolume />
        )}
      </div>
    </div>
  );
}
const MapWithTooltip = withSegmentTooltip(Map);

export function WorkflowPlanning() {
  const maximize = useAppSelector(selectMaximize);
  const project = useAppSelector(selectProject);

  const { map_style } = workflowStyles(maximize);

  // ======================

  const typicalMonth = useAppSelector(selectTypicalMonth);
  const { data: typicalMonthsInfo } = useGetTypicalMonthsQuery(project);
  const [layer, setLayer] = useState(null);
  const dispatch = useAppDispatch();

  // memoize so this doesn't change every render.
  const memoizedLayer = useMemo(() => ({ layer, setLayer }), [layer]);

  useEffect(() => {
    let new_layer = null;
    if (typicalMonthsInfo && typicalMonthsInfo.length > 0) {
      if (typicalMonth) {
        new_layer = typicalMonthsInfo.find(
          (info) => info.typicalMonth === typicalMonth,
        )?.layer;
      } else {
        dispatch(setTypicalMonth(typicalMonthsInfo[0].typicalMonth));
      }
    }
    // console.log(`WorkflowPlan set new layer ${new_layer} for typicalMonth ${typicalMonth}`);
    setLayer(new_layer);
  }, [typicalMonthsInfo, typicalMonth, setLayer, dispatch]);

  // ======================

  return (
    <LayerContext.Provider value={memoizedLayer}>
      <MapContextProvider>
        <div className={styles.workspace_container}>
          <div className={styles.workspace_content}>
            <div className={map_style}>
              <MapWithTooltip />
            </div>
            <VisualisationRegion />
          </div>
          <TaskBar segmentInfo="segment">
            <PlanningMonthPicker />
            <BinSizePicker />
            <CapacityThresholdEditor />
          </TaskBar>
        </div>
        <ScreenshotChart>
          <VisualisationRegion overrideVizMode={VIZ_MODE_CHART} />
        </ScreenshotChart>
      </MapContextProvider>
    </LayerContext.Provider>
  );
}
